
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { User } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const NAME_STOPWORDS = new Set(["da", "de", "do", "das", "dos", "e", "&"]);

const ProfileMenu = () => {
  const { user, signOut, userRole } = useAuth();
  const [open, setOpen] = useState(false);

  // Fornecedor: a sigla segue o nome da empresa (ex.: "The Pork's" -> TP).
  const { data: supplierName } = useQuery({
    queryKey: ["profile-menu-supplier-name", user?.id],
    enabled: !!user?.id && userRole === "supplier",
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("suppliers")
        .select("name")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data?.name ?? null;
    },
  });

  const getInitials = (name: string = "") => {
    const words = name
      .split(/\s+/)
      .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
      .filter(Boolean);
    const significant = words.filter((w) => !NAME_STOPWORDS.has(w.toLowerCase()));
    const picked = significant.length > 0 ? significant : words;
    if (picked.length === 0) {
      return user?.email ? user.email.substring(0, 2).toUpperCase() : "";
    }
    if (picked.length === 1) return picked[0].substring(0, 2).toUpperCase();
    return (picked[0][0] + picked[1][0]).toUpperCase();
  };

  const handleSignOut = async () => {
    await signOut();
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center space-x-2 focus:outline-none">
          <Avatar className="h-9 w-9 md:h-10 md:w-10 cursor-pointer hover:opacity-80">
            <AvatarFallback className="bg-gradient-to-r from-[#FF5E0E] via-[#D63384] to-[#7C3AED] text-white font-semibold">
              {user ? getInitials(supplierName || user?.user_metadata?.full_name) : <User size={16} />}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <Link to="/fornecedor/painel/inicio" onClick={() => setOpen(false)}>
          <DropdownMenuItem className="cursor-pointer">
            Perfil
          </DropdownMenuItem>
        </Link>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer" onClick={handleSignOut}>
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileMenu;
